import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { InviteFormComponent } from 'app/modules/invite/components/invite-form/invite-form.component';
import { Observable, Subject, filter, map, takeUntil, BehaviorSubject, merge, shareReplay, switchMap, tap, debounceTime } from 'rxjs';
import { UserService } from 'app/services/user.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Project, ProjectStatus } from 'app/models/project/project';
import { OrganizationService } from 'app/services/organization.service';
import { AddNewTaskComponent } from 'app/modules/organization/modules/tasks/components/add-new-task/add-new-task.component';
import { AddNewProjectComponent } from 'app/modules/organization/modules/projects/components/add-new-project/add-new-project.component';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { ProjectService } from 'app/services/project.service';


export enum OnBoardingStatus {
  OrganizationCreate = "organization: create",
  // IndustryCreate = "industry: create",
  UsersInvite = 'users: invite',
  ProjectCreate = 'project: create',
  TicketCreate = 'ticket: create',
  DownloadApp = 'app: download'
}

@Component({
  selector: 'tm-onboarding',
  templateUrl: './onboarding.component.html',
  styleUrls: ['./onboarding.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OnboardingComponent implements OnInit, OnDestroy {
  @ViewChild('infoProject', { static: true }) infoProject: TemplateRef<any>;
  readonly isOrganizationOwner$ = this.userService.isOrganizationOwner$.pipe(shareReplay(1));
  readonly isCreateProject$ = new BehaviorSubject<boolean>(false);
  readonly isInviteUsers$ = new BehaviorSubject<boolean>(false);
  readonly isCreateTicket$ = new BehaviorSubject<boolean>(false);
  readonly isDownloadApp$ = new BehaviorSubject<boolean>(false);
  private readonly destroy$ = new Subject<void>();
  private readonly _submitting$ = new BehaviorSubject<boolean>(false);
  private readonly organizationSelectedId = this.organizationService.organizationSelectedId;

  readonly submitting$ = this._submitting$.asObservable().pipe(
    shareReplay({ bufferSize: 1, refCount: true })
  );

  get completedSteps() {
    return Array.from(new Set([...(this.organizationService.organizationSelected?.onboarding?.completedSteps || [])])) || [];
  }

  get onboardingStatus() {
    return this.organizationService.onboardingStatus;
  }
  // get onboardingStatusPercent() {
  //   return this.organizationService.onboardingStatusPercent;
  // }

  constructor(
    private readonly dialog: MatDialog,
    private readonly router: Router,
    private readonly organizationService: OrganizationService,
    private readonly userService: UserService,
    private snackBar: MatSnackBar,
    private readonly fuseNavigationService: FuseNavigationService,
    private readonly projectService: ProjectService) { this.stepTakenCheck(); }

  readonly progress$ = merge(this.isCreateProject$, this.isInviteUsers$, this.isCreateTicket$, this.isDownloadApp$).pipe(
    debounceTime(150),
    map(() => [this.isCreateProject$.value, this.isInviteUsers$.value, this.isCreateTicket$.value, this.isDownloadApp$.value]),
    map(stepsCompleted => stepsCompleted.filter(stepCompleted => stepCompleted)?.length),
    map(stepsCompletedCount => ((stepsCompletedCount + 1) / Object.keys(OnBoardingStatus).length) * 100),
    shareReplay({ bufferSize: 1, refCount: true })
  )

  readonly onboardingStatus$ = this.progress$.pipe(
    map(progress => {
      const completedSteps = (progress / 100) * Object.keys(OnBoardingStatus).length;
      return `${completedSteps}/${Object.keys(OnBoardingStatus).length}`
    })
  )

  private stepTakenCheck() {

    const isInviteUsers = this.completedSteps.findIndex(step => step === OnBoardingStatus.UsersInvite) > -1;
    this.isInviteUsers$.next(isInviteUsers);
    // this.organizationService.updateCompletedSteps(OnBoardingStatus.InviteUsers);

    const isCreateProject = this.completedSteps.findIndex(step => step === OnBoardingStatus.ProjectCreate) > -1;
    this.isCreateProject$.next(isCreateProject);
    // this.organizationService.updateCompletedSteps(OnBoardingStatus.CreateProject);

    const isAssignTask = this.completedSteps.findIndex(step => step === OnBoardingStatus.TicketCreate) > -1;
    this.isCreateTicket$.next(isAssignTask);
    // this.organizationService.updateCompletedSteps(OnBoardingStatus.AssignTask);

    const isDownloadApp = this.completedSteps.findIndex(step => step === OnBoardingStatus.DownloadApp) > -1;
    this.isDownloadApp$.next(isDownloadApp);

    // this.updateBadge();
  }

  createProject(): void {
    const dialog = this.dialog.open(AddNewProjectComponent, { data: { data: null, type: 'add' } }).afterClosed() as Observable<Project | undefined>;
    dialog.pipe(
      filter(data => !!data),
      switchMap(() => {
        const completedSteps = new Set(this.completedSteps);
        completedSteps.add(OnBoardingStatus.ProjectCreate);
        const data = {
          skipped: false,
          completed: false,
          completedSteps: Array.from(completedSteps)
        };
        return this.organizationService.completeOnboard(this.organizationSelectedId, data)
      })
    ).subscribe(() => {
      this.isCreateProject$.next(true);
      this.organizationService.updateCompletedSteps(OnBoardingStatus.ProjectCreate);
      this.updateBadge();
      this.projectService.refresh();
    });
  }

  inviteUsers(): void {
    const dialog = this.dialog.open(InviteFormComponent).addPanelClass(['min-w-80', 'max-w-full', 'w-full', 'h-full', 'sm:h-fit', 'sm:w-100', 'md:w-160']).afterClosed();
    dialog.pipe(
      filter(isUpadate => !!isUpadate),
      switchMap(() => {
        const completedSteps = new Set(this.completedSteps);
        completedSteps.add(OnBoardingStatus.UsersInvite);
        const data = {
          skipped: false,
          completed: false,
          completedSteps: Array.from(completedSteps)
        };
        return this.organizationService.completeOnboard(this.organizationSelectedId, data)
      })
    ).subscribe(() => {
      this.isInviteUsers$.next(true);
      this.organizationService.updateCompletedSteps(OnBoardingStatus.UsersInvite);
      this.updateBadge();
    });
  }

  addTask(): void {
    const dialog = this.dialog.open(AddNewTaskComponent).addPanelClass(['min-w-80', 'max-w-full', 'w-full', 'h-full', 'sm:h-fit', 'sm:w-100', 'md:w-160']).afterClosed() as Observable<{ success: boolean, isProject: boolean } | undefined>;
    dialog.pipe(
      filter((data) => data.success),
      switchMap(({ isProject }) => {
        const completedSteps = new Set(this.completedSteps);
        completedSteps.add(OnBoardingStatus.TicketCreate);
        if (isProject) {
          completedSteps.add(OnBoardingStatus.ProjectCreate);
        }
        const data = {
          skipped: false,
          completed: false,
          completedSteps: Array.from(completedSteps)
        };
        return this.organizationService.completeOnboard(this.organizationSelectedId, data).pipe(map(() => isProject))
      })
    ).subscribe((isProject) => {
      this.isCreateTicket$.next(true);
      this.organizationService.updateCompletedSteps(OnBoardingStatus.TicketCreate);
      if (isProject) {
        this.isCreateProject$.next(true);
        this.organizationService.updateCompletedSteps(OnBoardingStatus.ProjectCreate);
      }
      this.updateBadge();
    });
  }

  downloadApp() {
    const completedSteps = new Set(this.completedSteps);
    completedSteps.add(OnBoardingStatus.DownloadApp);
    const data = {
      skipped: false,
      completed: false,
      completedSteps: Array.from(completedSteps)
    };
    this.organizationService.completeOnboard(this.organizationSelectedId, data).subscribe(() => {
      this.isDownloadApp$.next(true);
      this.organizationService.updateCompletedSteps(OnBoardingStatus.DownloadApp);
      this.updateBadge();
    })
  }

  completed(): void {
    this.isCreateProject$.next(true);
    this.isInviteUsers$.next(true);
    this.isCreateTicket$.next(true);
    this.isDownloadApp$.next(true);
  }

  private updateBadge(): void {
    const navComponent = this.fuseNavigationService.getComponent<FuseVerticalNavigationComponent>('mainNavigation');
    if (!navComponent) {
      return null;
    }
    const navigation = navComponent.navigation;
    const item = this.fuseNavigationService.getItem('onboarding', navigation);
    item.badge.title = this.onboardingStatus;
    navComponent.refresh();
  }

  private removeOnboardigMenu(): void {
    const navComponent = this.fuseNavigationService.getComponent<FuseVerticalNavigationComponent>('mainNavigation');
    if (!navComponent) {
      return null;
    }
    const navigation = navComponent.navigation;
    const item = this.fuseNavigationService.getItem('onboarding', navigation);
    item.hidden = () => true;
    navComponent.refresh();
  }

  ngOnInit(): void {
    this.progress$.pipe(
      takeUntil(this.destroy$),
      filter(progress => progress >= 99),
      tap(() => this._submitting$.next(true)),
      switchMap(() => {
        const data = {
          skipped: false,
          completed: true,
          completedSteps: [OnBoardingStatus.OrganizationCreate, OnBoardingStatus.ProjectCreate, OnBoardingStatus.TicketCreate, OnBoardingStatus.UsersInvite, OnBoardingStatus.DownloadApp],
        };
        return this.organizationService.completeOnboard(this.organizationSelectedId, data);
      })
    ).subscribe({
      next: () => {
        this.removeOnboardigMenu();
        this.organizationService.completedOnboarding();
        this.router.navigateByUrl('/dashboard');
      },
      error: () => {
        this._submitting$.next(false);
        this.snackBar.open('Failed completed onboarding! Try again later.', 'Ok');
        this.stepTakenCheck();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
